import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import cancelIcon from '../../assets/Icon/project/cancel.png';

export type RichTextEditorHandle = {
  openImagePicker: () => void;
  focus: () => void;
};

type Props = {
  value: string;
  onChange: (html: string) => void;
  placeholder: string;
  pictureIconSrc: string;
  maxImages: number;
  imageCount: number;
  pinnedImageSrc?: string;
  pinnedVideoEmbedUrl?: string;
  pinnedBadgeLabel?: string;
  showToolbar?: boolean;
  previewHeightPx?: number;
  onFocus?: () => void;
  onBlur?: () => void;
  /**
   * If true, 대표 이미지를 에디터(입력) 최상단에 고정 삽입합니다.
   * (요청: step3 input 제일 상단에 대표 이미지)
   */
  pinnedInsideEditor?: boolean;
  /**
   * Optional image upload handler.
   * If provided, this function is called when a file is selected.
   * It should upload the file and return the public URL.
   */
  onImageUpload?: (file: File) => Promise<string>;
};

/**
 * Lightweight Rich Text Editor (contentEditable)
 * - "사진 추가" 시 커서 위치에 이미지 삽입
 * - pinnedImageSrc가 있으면 대표 미디어를 입력 영역에 고정 표기
 */
const RichTextEditor = forwardRef<RichTextEditorHandle, Props>(function RichTextEditor(
  {
    value,
    onChange,
    placeholder,
    pictureIconSrc,
    maxImages,
    imageCount,
    pinnedImageSrc,
    pinnedVideoEmbedUrl,
    pinnedBadgeLabel,
    showToolbar = true,
    previewHeightPx = 240,
    onFocus,
    onBlur,
    pinnedInsideEditor = true,
    onImageUpload,
  }: Props,
  ref,
) {
  const { t } = useTranslation('translation');
  const editorRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const objectUrlsRef = useRef<string[]>([]);
  const resolvedPinnedBadgeLabel = pinnedBadgeLabel ?? t('common.labels.main');
  const hasPinnedMedia = Boolean(pinnedImageSrc || pinnedVideoEmbedUrl);

  const canAddImages = imageCount < maxImages;

  useImperativeHandle(
    ref,
    () => ({
      openImagePicker: () => {
        if (!canAddImages) return;
        const input = inputRef.current;
        if (!input) return;
        const picker = (input as HTMLInputElement & { showPicker?: () => void }).showPicker;
        if (typeof picker === 'function') {
          picker.call(input);
          return;
        }
        input.click();
      },
      focus: () => {
        const el = editorRef.current;
        if (!el) return;
        el.focus();
        const sel = window.getSelection?.();
        if (sel) {
          const range = document.createRange();
          range.setStart(el, 0);
          range.collapse(true);
          sel.removeAllRanges();
          sel.addRange(range);
        }
      },
    }),
    [canAddImages],
  );

  useEffect(() => {
    return () => {
      objectUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
      objectUrlsRef.current = [];
    };
  }, []);

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    const cur = serializeWithoutPinned(el);
    if (cur !== value) {
      el.innerHTML = value;
      // Inject delete buttons to existing figures
      el.querySelectorAll('figure[data-embed="story-image"]').forEach((fig) => {
        if (!fig.querySelector('button[data-embed="delete-btn"]')) {
          const delBtn = document.createElement('button');
          delBtn.setAttribute('data-embed', 'delete-btn');
          delBtn.type = 'button';
          const iconImg = document.createElement('img');
          iconImg.src = cancelIcon;
          iconImg.alt = t('common.actions.delete');
          iconImg.setAttribute('width', '32');
          iconImg.setAttribute('height', '32');
          delBtn.appendChild(iconImg);
          fig.appendChild(delBtn);
        }
        (fig as HTMLElement).contentEditable = 'false';
      });
    }
    setEmptyAttr(el, value);
  }, [value]);

  const insertImageAtCaret = (src: string) => {
    const el = editorRef.current;
    if (!el) return;

    const fig = document.createElement('figure');
    fig.setAttribute('data-embed', 'story-image');
    fig.contentEditable = 'false';

    const img = document.createElement('img');
    img.src = src;
    img.alt = '';
    img.setAttribute('data-embed', 'image');
    fig.appendChild(img);

    const delBtn = document.createElement('button');
    delBtn.setAttribute('data-embed', 'delete-btn');
    delBtn.type = 'button';
    const iconImg = document.createElement('img');
    iconImg.src = cancelIcon;
    iconImg.alt = t('common.actions.delete');
    iconImg.setAttribute('width', '32');
    iconImg.setAttribute('height', '32');
    delBtn.appendChild(iconImg);
    fig.appendChild(delBtn);

    const br = document.createElement('br');

    const sel = window.getSelection?.();
    if (!sel || sel.rangeCount === 0) {
      el.focus(); // Ensure focus
      el.appendChild(fig);
      el.appendChild(br);
      onChange(serializeWithoutPinned(el));
      return;
    }

    const range = sel.getRangeAt(0);
    if (!el.contains(range.commonAncestorContainer)) {
      el.focus(); // Ensure focus
      el.appendChild(fig);
      el.appendChild(br);
      onChange(serializeWithoutPinned(el));
      return;
    }

    range.deleteContents();
    range.insertNode(br.cloneNode()); // br after
    range.insertNode(fig);
    range.insertNode(br.cloneNode()); // br before

    // caret after the newly inserted figure and its following br
    const nextRange = document.createRange();
    nextRange.setStartAfter(fig.nextSibling || fig);
    nextRange.collapse(true);
    sel.removeAllRanges();
    sel.addRange(nextRange);

    const next = serializeWithoutPinned(el);
    setEmptyAttr(el, next);
    onChange(next);
  };

  const onFiles = async (files: FileList | null) => {
    if (!files) return;
    const getCurrentEmbeddedImageCount = () => {
      const el = editorRef.current;
      if (!el) return imageCount;
      return el.querySelectorAll('figure[data-embed="story-image"]').length;
    };

    // Use live DOM count as a source of truth to avoid stale-prop race conditions.
    const currentCount = Math.max(imageCount, getCurrentEmbeddedImageCount());
    const remaining = Math.max(0, maxImages - currentCount);
    if (remaining <= 0) return;
    const list = Array.from(files).slice(0, remaining);
    
    for (const f of list) {
      if (getCurrentEmbeddedImageCount() >= maxImages) break;
      try {
        let src = '';
        if (onImageUpload) {
          try {
            src = await onImageUpload(f);
          } catch (uploadErr) {
            console.error('Image upload failed:', uploadErr);
            alert(t('page.newProject.errors.imageReadFailed'));
            continue;
          }
        } else {
          // Fallback: read as base64
          src = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(f);
          });
        }

        if (src) {
          insertImageAtCaret(src);
        }
      } catch (err) {
        console.error('Failed to process file', err);
      }
    }
    
    // allow same file re-pick
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <Wrap>
      {!pinnedInsideEditor && hasPinnedMedia && (
        <PinnedMedia style={{ height: `${previewHeightPx}px` }}>
          {pinnedImageSrc ? (
            <img src={pinnedImageSrc} alt="" data-embed="image" />
          ) : pinnedVideoEmbedUrl ? (
            <iframe
              src={pinnedVideoEmbedUrl}
              title="Pinned video preview"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              data-embed="video"
            />
          ) : null}
          <div data-embed="badge">✓ {resolvedPinnedBadgeLabel}</div>
        </PinnedMedia>
      )}
      <EditorContainer>
        {pinnedInsideEditor && hasPinnedMedia && (
          <PinnedMedia style={{ height: `${previewHeightPx}px` }}>
            {pinnedImageSrc ? (
              <img src={pinnedImageSrc} alt="" data-embed="image" />
            ) : pinnedVideoEmbedUrl ? (
              <iframe
                src={pinnedVideoEmbedUrl}
                title="Pinned video preview"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                data-embed="video"
              />
            ) : null}
            <div data-embed="badge">✓ {resolvedPinnedBadgeLabel}</div>
          </PinnedMedia>
        )}
        <Editor
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          data-placeholder={placeholder}
          onFocus={() => {
            onFocus?.();
          }}
          onBlur={() => {
            onBlur?.();
          }}
          onInput={(e) => {
            const el = e.currentTarget as HTMLDivElement;
            const next = serializeWithoutPinned(el);
            setEmptyAttr(el, next);
            onChange(next);
          }}
          onClick={(e) => {
            const target = e.target as HTMLElement;
            const deleteBtn = target.closest('button[data-embed="delete-btn"]');
            if (deleteBtn) {
              const fig = deleteBtn.closest('figure');
              if (fig) {
                fig.remove();
                const el = editorRef.current;
                if (el) {
                  const next = serializeWithoutPinned(el);
                  setEmptyAttr(el, next);
                  onChange(next);
                }
              }
            }
          }}
        />
      </EditorContainer>

      {showToolbar ? (
        <Toolbar>
          <AddPhotoBtn
            type="button"
            onClick={() => {
              const input = inputRef.current;
              if (!input) return;
              const picker = (input as HTMLInputElement & { showPicker?: () => void }).showPicker;
              if (typeof picker === 'function') {
                picker.call(input);
                return;
              }
              input.click();
            }}
            disabled={!canAddImages}
          >
            <AddPhotoIcon src={pictureIconSrc} alt="" />
            사진 추가({imageCount}/{maxImages})
          </AddPhotoBtn>
        </Toolbar>
      ) : null}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
        onChange={(e) => onFiles(e.target.files)}
      />
    </Wrap>
  );
});

export default RichTextEditor;

function serializeWithoutPinned(el: HTMLDivElement) {
  const clone = el.cloneNode(true) as HTMLDivElement;
  clone.querySelectorAll(`button[data-embed="delete-btn"]`).forEach((n) => n.remove());
  // Do NOT aggressively clean BRs, as user might want them for spacing or next line input.
  // Only remove leading/trailing ones if they are clearly redundant.
  // Actually, let's keep them to satisfy "next line writing" requirement.
  return clone.innerHTML;
}

function setEmptyAttr(el: HTMLDivElement, html: string) {
  const stripped = html.replace(/&nbsp;/g, '').replace(/<br\s*\/?>/gi, '').trim();
  const isEmpty = stripped === '';
  el.dataset.empty = isEmpty ? 'true' : 'false';
}

const Wrap = styled.div`
  margin-top: 12px;
`;

const EditorContainer = styled.div`
  margin-top: -4px;
  width: 100%;
  min-height: 260px;
  border-radius: 10px;
  padding: 12px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
`;

const PinnedMedia = styled.figure`
  position: relative;
  margin: 0 0 12px;
  border-radius: 10px;
  overflow: hidden;
  border: 2.4px solid #2bc2e1;
  background: #f0f1f2;
  flex-shrink: 0;

  img[data-embed='image'] {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
  }

  iframe[data-embed='video'] {
    width: 100%;
    height: 100%;
    display: block;
    border: none;
  }

  div[data-embed='badge'] {
    position: absolute;
    top: 12px;
    left: 12px;
    display: flex;
    width: 64px;
    height: 28px;
    justify-content: center;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
    border-radius: 4px;
    padding: 2px 6px;
    background: ${({ theme }) => theme.colors.primaryBlue[40]};
    ${({ theme }) => theme.typography.caption.regular16}
    color: ${({ theme }) => theme.colors.grayScale[0]};
  }
`;

const Editor = styled.div`
  flex: 1;
  font-family: Pretendard;
  font-size: 20px;
  font-weight: 400;
  line-height: 150%;
  color: #17171b;
  outline: none;
  overflow: visible;

  &[data-empty='true']::before {
    content: attr(data-placeholder);
    color: #929292;
    font-size: 20px;
    font-weight: 400;
    line-height: 150%;
  }

  figure[data-embed='story-image'] {
    position: relative;
    margin: 12px 0;
    border-radius: 10px;
    overflow: hidden;
    border: 2.4px solid #2bc2e1;
    background: #f0f1f2;
  }

  figure[data-embed='story-image'] > img[data-embed='image'] {
    width: 100%;
    height: auto;
    display: block;
  }

  button[data-embed='delete-btn'] {
    position: absolute;
    top: 10px;
    right: 10px;
    width: 32px;
    height: 32px;
    padding: 0;
    border: none;
    border-radius: 4px;
    background: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;

    img {
      display: block;
      object-fit: contain;
      width: 32px;
      height: 32px;
    }
  }
`;

const Toolbar = styled.div`
  margin-top: 16px;
  display: flex;
  justify-content: center;
`;

const AddPhotoBtn = styled.button`
  height: 56px;
  width: 100%;
  border-radius: 12px;
  border: 2px solid #2bc2e1;
  background: #eafbff;
  color: #2bc2e1;
  font-family: Pretendard;
  font-size: 20px;
  font-weight: 500;
  line-height: 150%;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;

const AddPhotoIcon = styled.img`
  width: 22px;
  height: 22px;
  object-fit: contain;
`;


